/**
 * Domínio: Auditoria (audit_log browser) — GUPM-138
 * --------------------------------------------------
 * Funções puras que traduzem uma linha do `audit_log` (mutação técnica de
 * uma tabela) para algo legível na aba /auditoria: nome do módulo/entidade,
 * payload formatado em pares chave/valor, e — para trocas de plantão — o
 * checklist embutido no payload.
 *
 * TS puro: sem React, sem Supabase, sem DOM. Testável com Vitest sem mock.
 */

import type { ChecklistResponse } from '@/domain/shift-handover/types';

/** Ações CRUD do trigger genérico. O audit_log também grava ações semânticas
 *  (ex.: `shift_totp_failed`, `shift_totp_lockout`), então `AuditEntry.action`
 *  é `string` — não um enum fechado. */
export type CrudAction = 'INSERT' | 'UPDATE' | 'DELETE';

/** Uma linha do `audit_log` já normalizada para a UI. */
export interface AuditEntry {
  id: string;
  ts: Date;
  /** Nome da tabela mutada (`readings`, `tarp_records`, `shift_handovers`…). */
  entity: string | null;
  entityId: string | null;
  action: string;
  /** `auth_user_id` do ator (nulo = sistema/trigger). */
  actorId: string | null;
  /** Nome resolvido do ator (via `app_users_public`); nulo = sistema/não resolvido. */
  actorName: string | null;
  payload: unknown;
  // Contexto da requisição (GUPM-335). Opcionais: linhas anteriores ao
  // GUPM-335 não têm, e a tela trata ausência como "não registrado".
  /** E-mail de `auth.users` no momento da ação — nunca digitado pelo cliente. */
  actorEmail?: string | null;
  /** Linha ANTES do UPDATE (segredos já redigidos no banco). */
  payloadBefore?: unknown;
  /** Colunas que mudaram no UPDATE (sem `updated_at`). */
  changedFields?: string[] | null;
  ip?: string | null;
  /** `x-forwarded-for` cru: o 1º item pode ter sido forjado pelo cliente. */
  ipCadeia?: string | null;
  userAgent?: string | null;
  /** Por onde a ação entrou: app, anonimo, servico, auth, banco. */
  canal?: string | null;
  /**
   * Sem requisição HTTP (canal banco/auth): usuário do banco + application_name
   * — separa cron, painel SQL e servidor de login. Não identifica a pessoa.
   */
  conexao?: string | null;
}

export interface EntityDescriptor {
  /** Módulo do app a que a entidade pertence (agrupa o filtro). */
  module: string;
  /** Rótulo singular legível em pt-BR. */
  label: string;
}

/**
 * Mapa entidade → módulo/rótulo. NÃO-EXAUSTIVO: entidade nova aparece no
 * filtro com rótulo cru (o `entity`) sem quebrar — a aba escala sozinha.
 */
const ENTITY_MAP: Record<string, EntityDescriptor> = {
  readings: { module: 'Instrumentação', label: 'Leitura' },
  instruments: { module: 'Instrumentação', label: 'Instrumento' },
  instrument_thresholds: { module: 'Instrumentação', label: 'Carta de Controle' },
  tarp_records: { module: 'TARP', label: 'Registro TARP' },
  shift_handovers: { module: 'Plantão', label: 'Troca de plantão' },
  contacts: { module: 'Contatos', label: 'Contato' },
  // `users` é o nome atual da tabela (renomeada de `engineers` no GUPM-104);
  // linhas antigas do audit_log ainda usam `engineers`/`app_users`.
  users: { module: 'Usuários', label: 'Usuário' },
  engineers: { module: 'Usuários', label: 'Usuário' },
  app_users: { module: 'Usuários', label: 'Usuário' },
  banners_persistentes: { module: 'Dashboard', label: 'Banner' },
  recomendacoes: { module: 'Conformidade', label: 'Recomendação' },
  // Ficha regulatória da barragem: DPA, CRI e resolução ANM aplicável — é o que
  // define o regime de fiscalização, muda raro e sempre na mão (GUPM-141).
  dams: { module: 'Barragens', label: 'Barragem' },
  planos_acao: { module: 'Planos de Ação', label: 'Plano de Ação' },
  // Marca d'água do export diário da trilha; entra na tela pelo watchdog, que
  // grava `export_sharepoint_defasado` quando o export para (GUPM-264).
  audit_export_runs: { module: 'Auditoria', label: 'Export SharePoint' },
  gpx_monitoramento_raw: { module: 'Instrumentação', label: 'Feed GPX' },
  // GUPM-335 — eventos de acesso e do app (RPC `registrar_evento_app` e o
  // trigger de `auth.sessions`).
  auth: { module: 'Acesso', label: 'Sessão' },
  app: { module: 'Acesso', label: 'Uso do app' },
  // GUPM-335 — cadastros que ganharam trigger.
  tarps: { module: 'TARP', label: 'Catálogo de TARP' },
  catalogo_anomalias: { module: 'TARP', label: 'Catálogo de anomalias' },
  shift_checklist_items: { module: 'Plantão', label: 'Item do checklist' },
  zas_edificacoes: { module: 'PAEBM', label: 'Edificação na ZAS' },
  pontos_encontro: { module: 'PAEBM', label: 'Ponto de encontro' },
};

/** Entidades com rótulo conhecido — base das opções do filtro da /auditoria. */
export function entidadesConhecidas(): Array<[string, EntityDescriptor]> {
  return Object.entries(ENTITY_MAP);
}

/** Descreve uma entidade do audit_log; desconhecida cai num rótulo seguro. */
export function describeEntity(entity: string | null): EntityDescriptor {
  if (!entity) return { module: '—', label: 'Sistema' };
  return ENTITY_MAP[entity] ?? { module: '—', label: entity };
}

export interface PayloadField {
  key: string;
  value: string;
}

/**
 * Chaves sensíveis nunca exibidas no cliente — defesa em profundidade. O
 * segredo já é redigido na ESCRITA pelo trigger (GUPM-144) e o histórico foi
 * limpo (scrub); esta é a 2ª camada, espelhando a denylist `audit_secret_keys()`
 * do DB. Estender aqui + no DB ao surgir nova coluna-segredo.
 */
const REDACTED_KEYS = new Set(['totp_secret_base32']);

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * Achata um payload jsonb em pares chave/valor ordenados por chave, omitindo
 * chaves sensíveis (`REDACTED_KEYS`). `null`/vazio/não-objeto → lista vazia
 * (nunca lança).
 */
export function formatPayload(payload: unknown): PayloadField[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  const obj = payload as Record<string, unknown>;
  return Object.keys(obj)
    .filter((key) => !REDACTED_KEYS.has(key))
    .sort()
    .map((key) => ({ key, value: formatValue(obj[key]) }));
}

/**
 * Extrai as respostas de checklist do payload de um `shift_handovers`.
 * Payload sem `checklist_items` (ou malformado) → `[]` (nunca lança).
 */
export function extractChecklistResponses(payload: unknown): ChecklistResponse[] {
  if (!payload || typeof payload !== 'object') return [];
  const items = (payload as Record<string, unknown>).checklist_items;
  if (!Array.isArray(items)) return [];
  return items.filter(
    (i): i is ChecklistResponse =>
      !!i && typeof i === 'object' && 'item_id' in i && 'answer' in i,
  );
}

/** Classe Tailwind por ação CRUD — verde/azul/vermelho. */
const ACTION_STYLE: Record<CrudAction, string> = {
  INSERT: 'text-green-400',
  UPDATE: 'text-blue-400',
  DELETE: 'text-red-400',
};

/** Rótulo do canal por onde a ação entrou (GUPM-335). */
const CANAL: Record<string, string> = {
  app: 'App (usuário logado)',
  anonimo: 'Antes do login',
  servico: 'Serviço (Edge Function ou script)',
  auth: 'Servidor de autenticação',
  banco: 'Banco (cron, migration ou SQL)',
};

export function rotuloDoCanal(canal: string): string {
  return CANAL[canal] ?? canal;
}

/**
 * IP da linha. As linhas de TOTP anteriores ao GUPM-335 não têm a coluna `ip`:
 * a verify-totp guardava o IP em `payload.requester_ip`. Sem esse recurso a
 * coluna IP aparecia vazia em todo o histórico de TOTP (revisão 03/10, L13).
 */
export function ipDaLinha(entry: { ip?: string | null; payload: unknown }): string | null {
  if (entry.ip) return entry.ip;
  const p = entry.payload;
  if (p && typeof p === 'object' && !Array.isArray(p)) {
    const legado = (p as Record<string, unknown>).requester_ip;
    if (typeof legado === 'string' && legado) return legado;
  }
  return null;
}

/** Cor da ação; ações semânticas (não-CRUD) caem num tom neutro (nunca quebra). */
export function actionStyle(action: string): string {
  return (ACTION_STYLE as Record<string, string>)[action] ?? 'text-slate-300';
}

/**
 * Rótulo do ator para exibição — nunca expõe o UUID cru. Precedência:
 *   1. nome resolvido (via `app_users_public`);
 *   2. `actor_id` presente mas sem nome → conta órfã: a conta saiu do cadastro,
 *      mas o audit trail imutável preservou a ação ("conta removida");
 *   3. sem `actor_id` e canal `anonimo` → alguém antes do login (GUPM-335):
 *      tentativa ou falha de login. Chamar de "sistema" apagaria o fato de que
 *      havia uma pessoa do outro lado;
 *   4. sem `actor_id` → ação de sistema/trigger ("sistema").
 * O caso (2) é comum quando contas de teste são removidas (GUPM-147); nomes
 * reais dependem de um seed de auditoria coerente (follow-up).
 */
export function actorDisplay(
  entry: Pick<AuditEntry, 'actorName' | 'actorId'> & Partial<Pick<AuditEntry, 'canal' | 'actorEmail'>>,
): string {
  if (entry.actorName) return entry.actorName;
  // A view de nomes só resolve cadastro ATIVO. Quem tentou entrar sem
  // cadastro (`login_sem_acesso`) ou foi inativado não está nela — mas o
  // e-mail foi gravado no momento da ação. Chamar de "conta removida" seria
  // afirmar algo que não aconteceu (revisão de cobertura 03/10, L5).
  if (entry.actorId && entry.actorEmail) return `${entry.actorEmail} (fora do cadastro ativo)`;
  if (entry.actorId) return 'conta removida';
  if (entry.canal === 'anonimo') return 'não identificado (antes do login)';
  return 'sistema';
}

